import { ELegalIdType } from '@constants';
import { DatePicker, Form, Input } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { StepWrapper } from '../../../styles';
import { TranslatedTitle } from '../components';

import { InputsWrapper, DocumentInputWrapper, SectionTitle } from './CompleteProfile.styles';

import {
  CountrySelector,
  SecondLastNameField,
  PhoneInput,
  LegalIdTypeSelector,
  AddressFieldsSection
} from '@/components/Form';
import { Box } from '@/components/Utils';
import { useLegalIdValidation } from '@/hooks';
import { useProfile } from '@/store';

export const CompleteProfile = () => {
  const { t } = useTranslation('StudentOnboardingPageTranslations');
  const { profile } = useProfile();
  const form = Form.useFormInstance();
  const formRegionConfig = Form.useWatch('regionConfig', form);
  const region = formRegionConfig ?? profile?.regionConfig;
  const { legalIdRule } = useLegalIdValidation({ form, region });

  const handleLegalIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const upperValue = e.target.value.toUpperCase();
    form.setFieldValue('legalId', upperValue);
  };

  return (
    <StepWrapper>
      <TranslatedTitle i18nKey="COMPLETE_PROFILE" />
      <Box flexDirection="column">
        <InputsWrapper>
          <Box flex={1} gap={8} flexDirection="column">
            <Form.Item
              label={t('NAME')}
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_NAME')
                }
              ]}
              name="name">
              <Input />
            </Form.Item>
          </Box>
          <Box flex={1} gap={8} flexDirection="column">
            <Form.Item
              label={t('FIRST_SURNAME')}
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_FIRST_SURNAME')
                }
              ]}
              name="lastName">
              <Input />
            </Form.Item>
            <SecondLastNameField labelSecondLastName={t('SECOND_SURNAME')} />
          </Box>
        </InputsWrapper>
        <InputsWrapper>
          <Box flex={1} gap={8} flexDirection="column">
            <Form.Item
              label={t('PHONE_NUMBER')}
              layout="vertical"
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_PHONE')
                }
              ]}
              name="phone">
              <PhoneInput placeholder={t('PHONE_NUMBER_PLACEHOLDER')} />
            </Form.Item>
          </Box>
        </InputsWrapper>
        <InputsWrapper>
          <Box flex={1} gap={8} flexDirection="column">
            <DocumentInputWrapper>
              <Form.Item
                label={t('IDENTIFICATION_DOCUMENT')}
                layout="vertical"
                name="legalIdType"
                className="type-selector"
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_DOCUMENT_TYPE')
                  }
                ]}
                initialValue={profile?.legalIdType || ELegalIdType.DNI_NIF_NIE}>
                <LegalIdTypeSelector region={region} />
              </Form.Item>
              <Form.Item
                name="legalId"
                className="number-input"
                dependencies={['legalIdType']}
                rules={[
                  {
                    required: true,
                    message: t('REQUIRED_IDENTIFICATION_DOCUMENT')
                  },
                  legalIdRule
                ]}>
                <Input
                  placeholder={t('IDENTIFICATION_DOCUMENT_PLACEHOLDER')}
                  onChange={handleLegalIdChange}
                />
              </Form.Item>
            </DocumentInputWrapper>
          </Box>
        </InputsWrapper>
        <InputsWrapper>
          <Box flex={1} gap={8} flexDirection="column">
            <Form.Item
              label={t('DATE_OF_BIRTH')}
              layout="vertical"
              rules={[
                {
                  required: true,
                  message: t('REQUIRED_BIRTHDAY')
                }
              ]}
              name="birthday">
              <DatePicker format="DD/MM/YYYY" maxDate={dayjs()} />
            </Form.Item>
          </Box>
        </InputsWrapper>
        <InputsWrapper>
          <Box flex={1} gap={8} flexDirection="column">
            <Form.Item
              label={t('BIRTH_COUNTRY')}
              layout="vertical"
              name={['userInfo', 'countryBirth']}>
              <CountrySelector disableDescription placeholder={t('BIRTH_COUNTRY_PLACEHOLDER')} />
            </Form.Item>
          </Box>
        </InputsWrapper>

        <SectionTitle>{t('ADDRESS_DATA')}</SectionTitle>

        <AddressFieldsSection form={form} />
      </Box>
    </StepWrapper>
  );
};
